import React, { useContext } from "react";
import {
  Button,
  HelpDrawerToggle,
  HelpDrawer,
  CloseIconThin,
} from "@cmsgov/ds-medicare-gov";
import { FormattedMessage } from "react-intl-phraseapp";
import { EnrollmentDownAlert, LoadingMask } from "../../components";
import {
  sendTealiumEvent,
  getOppositePlanType,
  useTranslate,
  isMedicareAdvantageType,
} from "../../helpers";
import { AppStore } from "../../app/store";
import {
  SearchResultsState,
  useSearchResultsHelper,
} from "./useSearchResultsHelper";
import { PlanType, ActionType } from "../../@types";
import { RouteComponentProps } from "react-router";

interface SRPHeaderProps extends RouteComponentProps {
  pageState: SearchResultsState;
  updatePageState: (x: Partial<SearchResultsState>) => void;
}

const SearchResultsPageHeader: React.FC<SRPHeaderProps> = ({
  pageState,
  updatePageState,
  ...props
}) => {
  const { state, dispatch } = useContext(AppStore.AppContext);
  const { clearPlansToCompare } = useSearchResultsHelper(
    state,
    dispatch,
    pageState
  );

  const t = useTranslate();

  const isMA = isMedicareAdvantageType(state.planType || PlanType.MAPD);

  const switchPlanType = (): void => {
    const oppositePlanType = getOppositePlanType(
      state.planType || PlanType.MAPD
    );

    const switchLink = `/search-results?plan_type=${oppositePlanType.toLowerCase()}&fips=${
      state.fips
    }`;

    clearPlansToCompare();
    updatePageState({ currentPage: 0 });

    props.history.push(switchLink);
    dispatch({
      type: ActionType.UPDATE_PLANTYPE,
      payload: oppositePlanType,
    });

    const tealiumEventLabel = !isMA
      ? "View Available Medicare Advantage Plans"
      : "View Available Drug Plans";
    sendTealiumEvent("Find Plans - Available Plans", tealiumEventLabel);
  };

  return (
    <>
      <EnrollmentDownAlert className="ds-u-margin-y--3 ds-u-margin-x--7" />
      <div className="SearchResults__tellMeMore mct-hide-print">
        <FormattedMessage
          id="search_results_page.escape_hatch.other_plans"
          values={{
            pluralPlanType: isMA
              ? t("search_results_page.escape_hatch.separate_drug_plans")
              : t("search_results_page.escape_hatch.medicare_plans"),
            helpDrawerToggleLink: (
              <HelpDrawerToggle
                helpDrawerOpen={pageState.showSwitchPlansHelp}
                showDrawer={(): void => {
                  // istanbul ignore next: tested elsewhere
                  if (!pageState.showSwitchPlansHelp)
                    sendTealiumEvent(
                      "Help Drawer",
                      "Tell Me More - Plan Results"
                    );
                  updatePageState({ showSwitchPlansHelp: true });
                }}
                inline
              >
                {t("search_results_page.escape_hatch.tell_me_more")}
              </HelpDrawerToggle>
            ),
          }}
        />

        <LoadingMask loading={pageState.loading} delay={500} />

        {pageState.showSwitchPlansHelp && (
          <div className="mct-c-help-drawer">
            <HelpDrawer
              heading={
                <FormattedMessage
                  id="search_results_page.drawer.title"
                  tagName="span"
                  values={{
                    pluralPlanType: isMA
                      ? t("search_results_page.escape_hatch.drug_plans")
                      : t("search_results_page.escape_hatch.medicare_plans"),
                  }}
                />
              }
              onCloseClick={(): void =>
                updatePageState({ showSwitchPlansHelp: false })
              }
              closeButtonText={<CloseIconThin />}
            >
              <h4 className="ds-u-margin-top--0">
                <FormattedMessage
                  id="search_results_page.drawer.subtitle"
                  tagName="span"
                  values={{
                    pluralPlanType: isMA
                      ? t("search_results_page.escape_hatch.drug_plans")
                      : t("search_results_page.escape_hatch.medicare_plans"),
                    count: pageState.otherPlansAvailable,
                  }}
                />
              </h4>

              {isMA && <p>{t("search_results_page.drawer.switch_to_pdp")}</p>}

              {!isMA && <p>{t("search_results_page.drawer.switch_to_mapd")}</p>}
              <p>{t("search_results.page.drawer.would_you_like_to_switch")}</p>

              <Button
                onClick={(): void => {
                  // istanbul ignore next: tested elsewhere
                  sendTealiumEvent(
                    "Help Drawer",
                    "Tell Me More - Plan Results - Yes"
                  );
                  updatePageState({ showSwitchPlansHelp: false });
                  switchPlanType();
                }}
                className="ds-u-margin-right--2"
              >
                {t("yes")}
              </Button>

              <Button
                onClick={(): void => {
                  sendTealiumEvent(
                    "Help Drawer",
                    "Tell Me More - Plan Results - No"
                  );
                  updatePageState({ showSwitchPlansHelp: false });
                }}
              >
                {t("no")}
              </Button>
            </HelpDrawer>
          </div>
        )}

        <Button
          variation="transparent"
          size="small"
          className="ds-u-padding--0 ds-u-text-align--right e2e-switch-plan-type"
          onClick={switchPlanType}
        >
          <FormattedMessage
            id="search_results_page.escape_hatch.switch_plans"
            tagName="span"
            values={{
              pluralPlanType: isMA
                ? t("search_results_page.escape_hatch.drug_plans")
                : t("search_results_page.escape_hatch.medicare_plans"),
              count: pageState.otherPlansAvailable,
            }}
          />
        </Button>
      </div>
    </>
  );
};

export default SearchResultsPageHeader;
